import { PropsWithChildren, useState } from "react";
import { type HttpError, useExport, useImport, useNavigation, useNotification, useTable, useTranslate } from "@refinedev/core";
import { CreateButton, ExportButton } from "@refinedev/mui";
import { useGo } from "@refinedev/core";
import {
  RefineListView,
} from "../../components";
import { useLocation } from "react-router";
import { CompanyListTable } from "../../components/company";
import { CompanyFormSchema } from "@/utils/zod/companyFormValidation";
import { ImportButton } from "@/components/override/ImportButton";
import { companyImportCSV } from "@/utils/data-provider/csvImportSchema";
import { Typography } from "@mui/material";

export const CompanyList = ({ children }: PropsWithChildren) => {
  const t = useTranslate();

  const { tableQuery } = useTable<CompanyFormSchema, HttpError, CompanyFormSchema>({
    resource: "companies",
  });

  const { createUrl } = useNavigation();
  const go = useGo();
  const { pathname } = useLocation();
  const { open } = useNotification();
  const [importProgress, setImportProgress] = useState<{
    totalAmount: number;
    processedAmount: number;
  }>();


  const { inputProps, isLoading: isImporting } = useImport({
    resource: "companies",
    onFinish: (data) => {
      data.succeeded?.[0]?.response.forEach((response) => {
        if (response.error) {
          open?.({
            message: "Import Error",
            description: response.error,
            type: "error",
          });
        } else {
          open?.({
            message: "Import Success",
            description: response.message,
            type: "success",
          });
        }
      });
      data.errored?.[0]?.response.forEach((response) => {
        open?.({
          message: "Import Error",
          description: response.error,
          type: "error",
        });
      });
    },
    onProgress: (data) => {
      setImportProgress(data);
    },
    mapData: (data) => {
      const { type, category, email, notes, pan_number, legal_name, ledger_name, agent_ledger_name, ...allAddresses  } = data;
      // Organize addresses by index
      const addressesByIndex: Record<string, Record<string, any>> = {};
      
      Object.entries(allAddresses).forEach(([key, value]) => {
        const match = key.match(/address_(\d+)_(.+)/);
        if (match) {
          const [_, indexStr, field] = match;
          const index = parseInt(indexStr) - 1;
          
          if (!addressesByIndex[index]) {
            addressesByIndex[index] = {};
          }
          
          addressesByIndex[index][field] = field === "gstin" ? value || null : value;
        }
      });
      
      return {
        ledger_name: ledger_name.trim().toUpperCase(),
        email: email,
        notes: notes,
        pan_number: pan_number || null,
        legal_name: legal_name,
        agent_ledger_name: agent_ledger_name,
        addresses: Object.values(addressesByIndex),
        type: type?.toLowerCase() || "customer",
        category: category?.toLowerCase(),
      }
    }
  });

  const { triggerExport, isLoading: isExporting } = useExport<CompanyFormSchema>({
    resource: "companies",
    download: true,
    mapData: (data) => {
      const { addresses, accounts, tags, ...rest } = data;
      const mappedData = {
        type: rest.type,
        category: rest.category,
        email: rest.email,
        notes: rest.notes,
        pan_number: rest.pan_number,
        legal_name: rest.legal_name,
        ledger_name: rest.ledger_name,
        agent_ledger_name: rest.agent_ledger_name,
        ...(addresses?.reduce((acc: Record<string, any>, address, index) => {
          Object.entries(address ?? {}).forEach(([key, value]) => {
            if (["id", "inserted_at", "updated_at", "line2", "line3"].includes(key)) return;
            acc[`address_${index + 1}_${key}`] = value;
          });
          return acc;
        }, {}))

      };
      return mappedData;
    }
  });

  return (
    <>
      <RefineListView
        headerButtons={(props) => [
          <Typography variant="body2" color="text.secondary"> 
            {isImporting ? `Importing... ${importProgress?.processedAmount}/${importProgress?.totalAmount}` : isExporting ? "Exporting..." : undefined} 
          </Typography>,
          <ExportButton onClick={triggerExport} disabled={isImporting || isExporting} />,
          <ImportButton inputProps={inputProps} csvHeaders={companyImportCSV} disabled={isImporting || isExporting} type="company"/>,
          <CreateButton
            {...props.createButtonProps}
            key="create"
            size="medium"
            sx={{ height: "40px" }}
            onClick={() => {
              return go({
                to: `${createUrl("companies")}`,
                query: {
                  to: pathname,
                },
                options: {
                  keepQuery: true,
                },
                type: "replace",
              });
            }}
          >
            {t("company.actions.add")}
          </CreateButton>,
        ]}
      >
        <CompanyListTable tableQuery={tableQuery} />
      </RefineListView>
      {children}
    </>
  );
};
